import { useQuery } from '@tanstack/react-query';
import queryKeys from '../reactQuery/queryKeys';
import getHotelById from './getHotelById';
import getHotelRoomsFromApi from './getHotelRooms';
import { Hotel } from '../isValidHotel';

type HotelRoomFromApi = Awaited<ReturnType<typeof getHotelRoomsFromApi>>[0];
type GetHotelByIdParameters = Parameters<typeof getHotelById>[0];
type GetHotelRoomParameters = Parameters<typeof getHotelRoomsFromApi>[0];

export type HotelResult = Hotel & {
  hotelRooms: HotelRoomFromApi[];
};

export type Params = GetHotelByIdParameters &
  GetHotelRoomParameters & { routerIsReady?: boolean; isTLLMaintenance?: boolean };

const fetchData = async (
  { hotelId, checkInDate, checkOutDate, guestCountString, roomCount, locale }: Params,
  signal?: AbortSignal
): Promise<HotelResult> => {
  const hotel = await getHotelById({ hotelId, locale }, signal);

  const hotelRooms = await getHotelRoomsFromApi(
    {
      hotelId,
      checkInDate,
      checkOutDate,
      guestCountString,
      roomCount,
      locale,
    },
    signal
  );

  return { ...hotel, hotelRooms };
};

const useGetHotelById = ({
  hotelId,
  checkInDate,
  checkOutDate,
  guestCountString,
  roomCount,
  locale,
  routerIsReady,
  isTLLMaintenance,
}: Params) => {
  const response = useQuery<HotelResult, Error>({
    queryKey: queryKeys.hotel({
      hotelId,
      checkInDate,
      checkOutDate,
      guestCountString,
      roomCount,
      locale,
    }),
    queryFn: ({ signal }) =>
      fetchData(
        { hotelId, checkInDate, checkOutDate, guestCountString, roomCount, locale },
        signal
      ),
    retry: false,
    keepPreviousData: false,
    enabled:
      !!hotelId &&
      !!checkInDate &&
      !!checkOutDate &&
      !!guestCountString &&
      !!roomCount &&
      !!locale &&
      !!routerIsReady &&
      !isTLLMaintenance,
  });
  return response;
};

export default useGetHotelById;
